// src/theme/runtime.ts  
import { themes } from "@/theme/themes";

// --- Utilities ---
function toHslTriplet(input: string): string | null {
  try {
    const el = document.createElement("div");
    el.style.color = input;
    document.body.appendChild(el);
    const rgb = getComputedStyle(el).color || "";
    document.body.removeChild(el);
    const m = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!m) return null;
    const [r, g, b] = [parseInt(m[1],10)/255, parseInt(m[2],10)/255, parseInt(m[3],10)/255];
    const max = Math.max(r,g,b), min = Math.min(r,g,b);
    let h = 0, s = 0, l = (max+min)/2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d/(2 - max - min) : d/(max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    const H = Math.round(h * 360);
    const S = Math.round(s * 100);
    const L = Math.round(l * 100);
    return `${H} ${S}% ${L}%`;
  } catch {
    return null;
  }
}

function toRgbTriplet(input: string): string | null {
  try {
    const el = document.createElement("div");
    el.style.color = input;
    document.body.appendChild(el);
    const rgb = getComputedStyle(el).color || "";
    document.body.removeChild(el);
    const m = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!m) return null;
    return `${parseInt(m[1], 10)}, ${parseInt(m[2], 10)}, ${parseInt(m[3], 10)}`;
  } catch {
    return null;
  }
}


const setCss = (name: string, val: any) => {
  try {
    const style = document.documentElement.style;
    if (val === undefined || val === null || val === "") {
      // Important: remove stale value so CSS var fallbacks can apply
      style.removeProperty(name);
    } else {
      style.setProperty(name, String(val));
    }
  } catch {}
};

const setHSL = (name: string, color: any) => {
  const t = color ? toHslTriplet(String(color)) : null;
  if (t) setCss(name, t);
};

// Ensure Awards tab tokens are always present (derive from Gallery / About if missing)
function normalizeTheme(input: Record<string, any>): Record<string, any> {
  const t = { ...(input || {}) };

  // ---- NAV aliases (Theme Preview keys → runtime keys) ----
  // Base color: accept navBaseBg or legacy navBg / navBgColor
  t.navBaseBg = t.navBaseBg ?? t.navBg ?? t.navBgColor ?? "#ffffff";

  // Opacity: accept navBgOpacity or legacy navOpacity (clamp 0..1)
  if (t.navBgOpacity == null && t.navOpacity != null) {
    const n = Number(t.navOpacity);
    if (!Number.isNaN(n)) t.navBgOpacity = n;
  }
  if (t.navBgOpacity != null) {
    const n = Number(t.navBgOpacity);
    if (!Number.isNaN(n)) t.navBgOpacity = Math.max(0, Math.min(1, n));
  }

  // Pattern url
  t.navPatternImage = t.navPatternImage ?? t.navPatternUrl ?? t.navPattern ?? "";

  // Remove/Transparent toggles (strings "yes"/"no")
  if (t.navPatternRemove == null && t.removePattern != null) t.navPatternRemove = t.removePattern;
  if (t.navPatternTransparent == null && t.patternTransparent != null) t.navPatternTransparent = t.patternTransparent;

  // Normalise toggles
  t.navPatternRemove = String(t.navPatternRemove ?? "no").toLowerCase() === "yes" ? "yes" : "no";
  t.navPatternTransparent = String(t.navPatternTransparent ?? "yes").toLowerCase() === "no" ? "no" : "yes";

  // Pattern size: clamp 1–100
  if (t.navPatternSize != null) {
    const n = Number(t.navPatternSize);
    if (!Number.isNaN(n)) t.navPatternSize = Math.max(1, Math.min(100, n));
  }

  // Pattern repeat normalize
  if (t.navPatternRepeat != null) {
    const v = String(t.navPatternRepeat).toLowerCase().trim();
    t.navPatternRepeat = ["repeat","no-repeat","repeat-x","repeat-y","space","round"].includes(v) ? v : "repeat";
  }

  // Pattern opacity: clamp 0..1
  if (t.navPatternOpacity != null) {
    const n = Number(t.navPatternOpacity);
    if (!Number.isNaN(n)) t.navPatternOpacity = Math.max(0, Math.min(1, n));
  }

  // ---- Awards sensible fallbacks (unchanged logic) ----
  t.awardsTabActiveBg     ??= t.galleryTabBg       ?? t.aboutBtnBg;
  t.awardsTabActiveText   ??= t.galleryTabText     ?? t.aboutBtnText     ?? "#ffffff";
  t.awardsTabInactiveBg   ??= t.galleryTabHoverBg  ?? t.aboutBtnHoverBg;
  t.awardsTabInactiveText ??= t.galleryTabHoverText?? t.aboutBtnHoverText ?? "rgb(26 26 26)";

  return t;
}

// --- The single place that writes ALL CSS variables your components use ---
export function applyThemeVars(theme: Record<string, any>) {
  // Raw keys for debugging
  const keys = [
    "background","fontBody","buttonBg","recognitionBtnText",
    "heroH1","heroH2","heroBtnBg","heroBtnText","heroBtnHoverBg","heroBtnHoverText",
    "aboutBtnBg","aboutBtnText","aboutBtnHoverBg","aboutBtnHoverText",
    "galleryCtaBg","galleryCtaText","galleryCtaHoverBg","galleryCtaHoverText",
    "pricingBtnBg","pricingBtnText","pricingBtnHoverBg","pricingBtnHoverText",
    "contactBtnBg","contactBtnText","contactBtnHoverBg","contactBtnHoverText","contactFormBg",
    "awardsTabActiveBg","awardsTabActiveText","awardsTabInactiveBg","awardsTabInactiveText",
    "clientBtnBg","clientBtnText","clientBtnHoverBg","clientBtnHoverText","clientName",
  ];
  for (const k of keys) setCss(`--${k}`, theme?.[k]);

  // Global
  setCss("--color-background", theme.background);
  setCss("--color-foreground", theme.fontBody);

  // Headings (non-hero)
  setCss("--heading-color", theme.buttonBg);

  // Recognition
  setCss("--recognition-btn-text", theme.recognitionBtnText);

  // NAV BAR (base color + opacity + pattern) — matches Header.tsx usage
  // Base color used via color-mix in Header.tsx
  setCss("--nav-base-bg", theme.navBaseBg ?? theme.navBg ?? theme.navBgColor ?? "#ffffff");
  setCss("--nav-bg-opacity", theme.navBgOpacity ?? 0.60); // 0..1
  
  // Pattern layer (separate overlay)
  {
    const remove = String(theme.navPatternRemove ?? "no").toLowerCase() === "yes";
    const img = theme.navPatternImage ? `url(${String(theme.navPatternImage)})` : "none";
    setCss("--nav-pattern", remove ? "none" : img);
  
    const transparent = String(theme.navPatternTransparent ?? "yes").toLowerCase() === "yes";
    setCss("--nav-pattern-bg", transparent ? "transparent" : "var(--nav-base-bg, #ffffff)");
  }
  setCss("--nav-pattern-size",   theme.navPatternSize ? `${String(theme.navPatternSize)}%` : "auto");
  setCss("--nav-pattern-repeat", theme.navPatternRepeat ?? "repeat");
  setCss("--nav-pattern-opacity", theme.navPatternOpacity ?? 1);

  // Hero
  setCss("--hero-h1", theme.heroH1);
  setCss("--hero-h2", theme.heroH2);
  setCss("--hero-btn-bg", theme.heroBtnBg ?? theme.buttonBg);
  setCss("--hero-btn-text", theme.heroBtnText);
  setCss("--hero-btn-hover-bg", theme.heroBtnHoverBg);
  setCss("--hero-btn-hover-text", theme.heroBtnHoverText);

  // About
  setCss("--about-btn-bg", theme.aboutBtnBg);
  setCss("--about-btn-text", theme.aboutBtnText);
  setCss("--about-btn-hover-bg", theme.aboutBtnHoverBg);
  setCss("--about-btn-hover-text", theme.aboutBtnHoverText);
  setCss("--about-card-tint", theme.aboutCardTint);

  // Gallery
  // The component checks for '*-active-*' and '*-inactive-*'
  // We set both the raw key and the explicit active/inactive CSS variables.
  setCss("--galleryTabBg", theme.galleryTabBg);
  setCss("--galleryTabText", theme.galleryTabText);
  setCss("--galleryTabHoverBg", theme.galleryTabHoverBg);
  setCss("--galleryTabHoverText", theme.galleryTabHoverText);
  
  setCss("--gallery-tab-active-bg", theme.galleryTabBg);
  setCss("--gallery-tab-active-text", theme.galleryTabText);
  setCss("--gallery-tab-inactive-bg", theme.galleryTabHoverBg);
  setCss("--gallery-tab-inactive-text", theme.galleryTabHoverText);

  setCss("--gallery-cta-bg", theme.galleryCtaBg);
  setCss("--gallery-cta-text", theme.galleryCtaText);
  setCss("--gallery-cta-hover-bg", theme.galleryCtaHoverBg);
  setCss("--gallery-cta-hover-text", theme.galleryCtaHoverText);

  // Awards tabs mirror Gallery tabs with safe fallbacks (handles partial promotions)
  const awardsActiveBg     = theme.galleryTabBg        ?? theme.awardsTabActiveBg     ?? theme.aboutBtnBg;
  const awardsActiveText   = theme.galleryTabText      ?? theme.awardsTabActiveText   ?? theme.aboutBtnText ?? "#ffffff";
  const awardsInactiveBg   = theme.galleryTabHoverBg   ?? theme.awardsTabInactiveBg   ?? theme.aboutBtnHoverBg;
  const awardsInactiveText = theme.galleryTabHoverText ?? theme.awardsTabInactiveText ?? theme.aboutBtnHoverText ?? "rgb(26 26 26)";
  
  setCss("--awards-tab-active-bg",     awardsActiveBg);
  setCss("--awards-tab-active-text",   awardsActiveText);
  setCss("--awards-tab-inactive-bg",   awardsInactiveBg);
  setCss("--awards-tab-inactive-text", awardsInactiveText);
  
  // Pricing (inherits About)
  setCss("--pricing-btn-bg", theme.aboutBtnBg);
  setCss("--pricing-btn-text", theme.aboutBtnText);
  setCss("--pricing-btn-hover-bg", theme.aboutBtnHoverBg);
  setCss("--pricing-btn-hover-text", theme.aboutBtnHoverText);

  // Contact (inherits About)
  setCss("--contact-btn-bg", theme.aboutBtnBg);
  setCss("--contact-btn-text", theme.aboutBtnText);
  setCss("--contact-btn-hover-bg", theme.aboutBtnHoverBg);
  setCss("--contact-btn-hover-text", theme.aboutBtnHoverText);
  
  // Form background inherits site background gradient
  setCss("--contact-form-bg", theme.background);

  // Client
  setCss("--client-btn-bg", theme.clientBtnBg ?? theme.galleryCtaBg);
  setCss("--client-btn-text", theme.clientBtnText ?? theme.galleryCtaText);
  setCss("--client-btn-hover-bg", theme.clientBtnHoverBg ?? theme.galleryCtaHoverBg);
  setCss("--client-btn-hover-text", theme.clientBtnHoverText ?? theme.galleryCtaHoverText);
  setCss("--client-name", theme.clientName);

  // shadcn tokens for default buttons/typography
  setHSL("--background", theme.background || "#ffffff");
  setHSL("--foreground", theme.fontBody   || "#111111");
  setHSL("--primary", theme.galleryCtaBg ?? theme.buttonBg ?? theme.heroBtnBg ?? "#e6a4a4");
  setHSL("--primary-foreground", theme.galleryCtaText ?? theme.fontBody ?? "#111111");

  try {
    if (theme.background) document.body.style.background = String(theme.background);
    if (theme.fontBody)   document.body.style.color      = String(theme.fontBody);
  } catch {}
}

// Try server → fallback to file-based production
// Try server → fallback to file-based production, but ALWAYS merge over file defaults
export async function loadLatestProductionTheme(): Promise<Record<string, any>> {
  const fileProd =
    (themes as any)?.production ??
    (themes as any)?.default ??
    (Object.values(themes || {})[0] || {});

  try {
    const res = await fetch(`/api/theme-history?t=${Date.now()}`, {
      method: "GET",
      cache: "no-store",
    });
    if (res.ok) {
      const j = await res.json();
      const latest = j && Array.isArray(j.items) && j.items[0] && j.items[0].theme ? j.items[0].theme : null;

      if (latest && typeof latest === "object") {
        return normalizeTheme({ ...fileProd, ...latest });
      }
    }
  } catch {
    /* ignore */
  }

  return normalizeTheme(fileProd || {});
}

// Call once at app start
export async function ensureProductionThemeApplied() {
  const theme = await loadLatestProductionTheme();
  applyThemeVars(theme);
}

// Optional: listen for a cross-tab/page refresh event
export function attachThemeRefreshListener() {
  const handler = async () => {
    const theme = await loadLatestProductionTheme();
    applyThemeVars(theme);
  };
  window.addEventListener("theme:refresh", handler);
  return () => window.removeEventListener("theme:refresh", handler);
}

// ----- Boot-time apply + listen (safe if imported on client) -----
if (typeof window !== "undefined") {
  // apply immediately on first load
  ensureProductionThemeApplied();
  // keep in sync if another tab/page promotes a theme and dispatches theme:refresh
  attachThemeRefreshListener();
}
